import { useState, useCallback } from 'react';

import { registerCustomFilters } from '@utils/liquidFilters';
import { Liquid } from 'liquidjs';

/**
 * useLiquidRenderer - Hook for rendering Liquid templates
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.sampleData - Sample data for rendering (can be updated externally)
 *
 * @returns {Object} - { render, rendering, error, html, clearError }
 */
export default function useLiquidRenderer({ sampleData } = {}) {
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState(null);
  const [html, setHtml] = useState('');

  /**
   * Render Liquid template with given content and data
   * @param {string} content - Liquid template content
   * @param {Object} data - Data for rendering (optional, defaults to sampleData from hook options)
   * @returns {Promise<string>} - Rendered HTML
   */
  const render = useCallback(async (content, data = sampleData) => {
    if (!content || !content.trim()) {
      setHtml('');
      setError(null);
      return '';
    }

    if (!data) {
      const errorMsg = 'Sample data is required for rendering';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    setRendering(true);
    setError(null);

    try {
      const engine = new Liquid();
      registerCustomFilters(engine);
      const renderedHtml = await engine.parseAndRender(content, data);
      setHtml(renderedHtml);
      return renderedHtml;
    } catch (err) {
      console.error('Liquid render error:', err);
      setError(err.message);
      throw err;
    } finally {
      setRendering(false);
    }
  }, [sampleData]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    render,
    rendering,
    error,
    html,
    clearError
  };
}
